// Bilingual content for the Lowland Hunting landing page.
// Structure is intentionally content-first so future pages can reuse the same shape.

// Imported rather than referenced by URL so the bundler fingerprints them and
// they land in /static/, where the immutable cache rule applies. These are
// stand-in Unsplash photographs — replace with the estate's own.
import heroImg from "@/assets/hero.jpg";
import card1Img from "@/assets/card-1.jpg";
import card2Img from "@/assets/card-2.jpg";
import card3Img from "@/assets/card-3.jpg";
import manifestoImg from "@/assets/manifesto.jpg";
import contactImg from "@/assets/contact.jpg";

export const translations = {
  ro: {
    nav: {
      links: [
        { label: "Acasă", href: "#top" },
        { label: "Tarife", href: "#tarife" },
        { label: "Vânători", href: "#experiences" },
        { label: "Tradiție", href: "#manifesto" },
        { label: "Contact", href: "#contact" },
      ],
      phone: "+40 726 000 000",
      menu: "Meniu",
      close: "Închide",
    },
    hero: {
      overline: "Estate de vânătoare · Câmpia Română",
      titleLines: ["Vânătoare", "la Șes"],
      script: "Tradiția câmpiei, la răsărit.",
      cta: "Vezi tarifele",
      scroll: "Derulează",
    },
    selection: {
      label: "Alege vânătoarea",
      fields: {
        region: {
          placeholder: "Zonă",
          options: ["Câmpia Română", "Lunca Dunării", "Bărăgan"],
        },
        game: {
          placeholder: "Specie",
          options: ["Fazan", "Rațe & Gâște", "Prepeliță", "Iepure de câmp"],
        },
        season: {
          placeholder: "Sezon",
          options: ["Toamnă", "Iarnă"],
        },
      },
      cta: "Caută",
    },
    marquee: ["Vânătoare", "Tradiție", "Câmpie", "Răsărit", "Miriște", "Luncă"],
    manifesto: {
      overline: "Manifest",
      title: "Câmpia are propriul ei ritual",
      chapters: [
        {
          no: "01",
          title: "Răsăritul",
          body: "Totul începe înainte de lumină. Ceața se ridică de pe miriște, iar câmpul respiră. Aici vânătoarea nu se grăbește — se așteaptă.",
        },
        {
          no: "02",
          title: "Câinele",
          body: "Un partener, nu un instrument. Fiecare oprire, fiecare adulmecare pe mirişte scrie povestea zilei. Îl urmezi și înveți câmpia prin el.",
        },
        {
          no: "03",
          title: "Respectul",
          body: "Etică, măsură și grijă pentru teren. Luăm doar ce oferă sezonul și lăsăm câmpia mai bogată decât am găsit-o.",
        },
      ],
    },
    experiences: {
      overline: "Trasee de vânătoare",
      title: "Trei vânători pe șes",
      items: [
        {
          no: "01",
          title: "Fazan pe Miriște",
          desc: "Vânătoare clasică cu câini de aret pe miriștile întinse ale Câmpiei Române, în lumina joasă a dimineții.",
          meta: [
            { k: "Durată", v: "2 zile" },
            { k: "Sezon", v: "1 oct — 15 dec" },
            { k: "Teren", v: "≈ 1.200 ha" },
          ],
          cta: "Detalii despre traseu",
        },
        {
          no: "02",
          title: "Rațe în Luncă",
          desc: "La pândă în lunca Dunării, printre bălți și stufăriș, la răsărit și în amurg — pentru rațe și gâște de pasaj.",
          meta: [
            { k: "Durată", v: "3 zile" },
            { k: "Sezon", v: "15 sep — 31 ian" },
            { k: "Teren", v: "Bălți & canale" },
          ],
          cta: "Detalii despre traseu",
        },
        {
          no: "03",
          title: "Prepeliță în Câmp",
          desc: "Vânătoare mobilă, în echipă mică, pe câmpul deschis al Bărăganului — dinamică, sportivă, la pas cu câinele.",
          meta: [
            { k: "Durată", v: "1 zi" },
            { k: "Sezon", v: "20 aug — 30 sep" },
            { k: "Teren", v: "≈ 800 ha" },
          ],
          cta: "Detalii despre traseu",
        },
      ],
    },
    pricing: {
      overline: "Tarife",
      title: "Cât costă",
      body: "Prețurile de mai jos sunt per vânător, pentru pachetul standard. Cazarea nu este inclusă — suntem agenție de vânătoare, nu pensiune.",
      groups: {
        migratory: "Păsări migratoare",
        small_game: "Vânat mic",
        big_game: "Vânat mare",
      },
      season: "Sezon",
      yearRound: "Tot timpul anului",
      days: (n) => `${n} ${n === 1 ? "zi" : "zile"} de vânătoare`,
      outings: (n) => `${n} ieșiri pe zi`,
      group: "Grup",
      from: "de la",
      perHunter: "/ vânător",
      perPiece: "/ piesă",
      onRequest: "La cerere",
      packageFrom: "Pachet de la",
      includes: "Include",
      trophyFee: "Tarif de împușcare, după trofeu",
      adder: "adaos",
      cull: "Selecție",
      wounded: (pct) => `Piesa rănită și nerecuperată se taxează cu ${pct}% din valoarea estimată a trofeului.`,
      woundedFlat: (v) => `Piesa rănită și nerecuperată se taxează cu ${v} €.`,
      missedShot: (v) => `Focul greșit se taxează cu ${v} €.`,
      empty: "Tarifele se actualizează. Scrie-ne pentru o ofertă.",
      cta: "Cere ofertă",
      configure: "Calculează prețul",
    },
    services: {
      transport_domain: "Transport în domeniul de vânătoare",
      pro_guide: "Ghid profesionist de vânătoare",
      guide_beaters_dogs: "Ghid profesionist + gonaci cu câini",
      export_docs: "Documente pentru exportul vânatului",
      trophy_prep_export: "Prepararea trofeelor și documente pentru export",
      formalities: "Toate formalitățile pentru vânătoare în România",
    },
    configurator: {
      overline: "Configurator",
      title: "Calculează-ți vânătoarea",
      body: "Alege specia, câți sunteți și câte zile. Prețul apare pe loc.",
      species: "Specie",
      hunters: "Vânători",
      days: "Zile",
      extras: "Opțiuni suplimentare",
      total: "Total grup",
      perHunter: "/ vânător",
      custom: "Pentru această configurație îți facem ofertă personalizată.",
      customCta: "Cere ofertă personalizată",
      cta: "Trimite cererea",
      note: "Prețurile nu includ cazarea.",
    },
    seasons: {
      overline: "Legal",
      title: "Sezoane și cote",
      body: "Perioadele legale de vânătoare și cota zilnică per vânător, conform reglementărilor în vigoare.",
      species: "Specie",
      period: "Sezon",
      quota: "Cotă zilnică",
      pieces: "buc.",
    },
    gallery: {
      overline: "Galerie",
      title: "Câmpia, în imagini",
      body: "Peisaje și momente de pe domeniile unde vânăm.",
      close: "Închide",
      prev: "Înapoi",
      next: "Înainte",
    },
    form: {
      overline: "Cerere",
      title: "Spune-ne ce te interesează",
      name: "Nume",
      email: "Email",
      phone: "Telefon",
      guests: "Număr de vânători",
      message: "Mesaj",
      messagePlaceholder: "Ce specie, ce perioadă, câte zile…",
      optional: "opțional",
      submit: "Trimite cererea",
      sending: "Se trimite…",
      success: "Cererea a plecat. Te contactăm în cel mult 24 de ore.",
      error: "Nu am putut trimite cererea. Încearcă din nou sau scrie-ne direct pe email.",
      rateLimited: "Ai trimis deja mai multe cereri. Încearcă mai târziu sau sună-ne.",
      required: "Completează numele și un email valid.",
    },
    contact: {
      overline: "Rezervări",
      title: "Pregătit pentru câmpie?",
      body: "Spune-ne ce specie și ce sezon te interesează, iar noi îți construim vânătoarea potrivită pe șes.",
      cta: "Contactează-ne",
      email: "rezervari@probeniesto.ro",
    },
    footer: {
      tagline: "Estate de vânătoare la șes.",
      rights: "Toate drepturile rezervate.",
      cols: [
        { title: "Vânători", links: ["Fazan", "Rațe & Gâște", "Prepeliță", "Iepure"] },
        { title: "Estate", links: ["Despre noi", "Etică & reguli", "Cazare", "Galerie"] },
        { title: "Contact", links: ["Rezervări", "Telefon", "Email", "Locație"] },
      ],
    },
  },
  en: {
    nav: {
      links: [
        { label: "Home", href: "#top" },
        { label: "Rates", href: "#tarife" },
        { label: "Hunts", href: "#experiences" },
        { label: "Tradition", href: "#manifesto" },
        { label: "Contact", href: "#contact" },
      ],
      phone: "+40 726 000 000",
      menu: "Menu",
      close: "Close",
    },
    hero: {
      overline: "Hunting Estate · The Romanian Plain",
      titleLines: ["Lowland", "Hunting"],
      script: "The tradition of the plains, at dawn.",
      cta: "Explore the hunts",
      scroll: "Scroll",
    },
    selection: {
      label: "Choose your hunt",
      fields: {
        region: {
          placeholder: "Region",
          options: ["Romanian Plain", "Danube Floodplain", "Bărăgan Steppe"],
        },
        game: {
          placeholder: "Game",
          options: ["Pheasant", "Ducks & Geese", "Quail", "Field Hare"],
        },
        season: {
          placeholder: "Season",
          options: ["Autumn", "Winter"],
        },
      },
      cta: "Find a hunt",
    },
    marquee: ["Hunting", "Tradition", "Plains", "Dawn", "Stubble", "Floodplain"],
    manifesto: {
      overline: "Manifesto",
      title: "The plain keeps its own ritual",
      chapters: [
        {
          no: "01",
          title: "The Dawn",
          body: "It all begins before the light. Mist lifts off the stubble and the field breathes. Out here the hunt is never rushed — it is awaited.",
        },
        {
          no: "02",
          title: "The Dog",
          body: "A partner, never a tool. Every point, every scent along the stubble writes the story of the day. You follow, and learn the plain through him.",
        },
        {
          no: "03",
          title: "The Respect",
          body: "Ethics, restraint and care for the land. We take only what the season offers and leave the plain richer than we found it.",
        },
      ],
    },
    experiences: {
      overline: "Hunting routes",
      title: "Three hunts on the plain",
      items: [
        {
          no: "01",
          title: "Pheasant on Stubble",
          desc: "A classic hunt with pointing dogs across the wide stubble fields of the Romanian Plain, in the low light of morning.",
          meta: [
            { k: "Length", v: "2 days" },
            { k: "Season", v: "Oct 1 — Dec 15" },
            { k: "Ground", v: "≈ 1,200 ha" },
          ],
          cta: "Read more",
        },
        {
          no: "02",
          title: "Ducks on Floodplain",
          desc: "Waiting in the Danube floodplain, among ponds and reeds, at first light and dusk — for passing ducks and geese.",
          meta: [
            { k: "Length", v: "3 days" },
            { k: "Season", v: "Sep 15 — Jan 31" },
            { k: "Ground", v: "Ponds & channels" },
          ],
          cta: "Read more",
        },
        {
          no: "03",
          title: "Quail in the Open",
          desc: "A mobile, small-team hunt across the open Bărăgan fields — dynamic and sporting, walking in step with the dog.",
          meta: [
            { k: "Length", v: "1 day" },
            { k: "Season", v: "Aug 20 — Sep 30" },
            { k: "Ground", v: "≈ 800 ha" },
          ],
          cta: "Read more",
        },
      ],
    },
    pricing: {
      overline: "Rates",
      title: "What it costs",
      body: "Prices below are per hunter, for the standard package. Accommodation is not included — we are a hunting agency, not a lodge.",
      groups: {
        migratory: "Migratory birds",
        small_game: "Small game",
        big_game: "Big game",
      },
      season: "Season",
      yearRound: "All year round",
      days: (n) => `${n} hunting ${n === 1 ? "day" : "days"}`,
      outings: (n) => `${n} outings per day`,
      group: "Group",
      from: "from",
      perHunter: "/ hunter",
      perPiece: "/ piece",
      onRequest: "On request",
      packageFrom: "Package from",
      includes: "Includes",
      trophyFee: "Trophy fee, by trophy size",
      adder: "surcharge",
      cull: "Cull",
      wounded: (pct) => `A wounded and unrecovered animal is charged at ${pct}% of the estimated trophy value.`,
      woundedFlat: (v) => `A wounded and unrecovered animal is charged €${v}.`,
      missedShot: (v) => `A missed shot is charged €${v}.`,
      empty: "Rates are being updated. Write to us for an offer.",
      cta: "Request an offer",
      configure: "Calculate the price",
    },
    services: {
      transport_domain: "Transport within the hunting grounds",
      pro_guide: "Professional hunting guide",
      guide_beaters_dogs: "Professional guide + beaters with dogs",
      export_docs: "Documents for exporting the game",
      trophy_prep_export: "Trophy preparation and export documents",
      formalities: "All formalities for hunting in Romania",
    },
    configurator: {
      overline: "Configurator",
      title: "Build your hunt",
      body: "Pick the game, how many of you and how many days. The price appears instantly.",
      species: "Game",
      hunters: "Hunters",
      days: "Days",
      extras: "Additional options",
      total: "Group total",
      perHunter: "/ hunter",
      custom: "For this configuration we will prepare a tailored offer.",
      customCta: "Request a tailored offer",
      cta: "Send the request",
      note: "Prices do not include accommodation.",
    },
    seasons: {
      overline: "Legal",
      title: "Seasons and bag limits",
      body: "Legal hunting periods and the daily bag limit per hunter, under current regulations.",
      species: "Species",
      period: "Season",
      quota: "Daily limit",
      pieces: "pcs.",
    },
    gallery: {
      overline: "Gallery",
      title: "The plain, in pictures",
      body: "Landscapes and moments from the grounds we hunt.",
      close: "Close",
      prev: "Previous",
      next: "Next",
    },
    form: {
      overline: "Enquiry",
      title: "Tell us what you have in mind",
      name: "Name",
      email: "Email",
      phone: "Phone",
      guests: "Number of hunters",
      message: "Message",
      messagePlaceholder: "Which game, which period, how many days…",
      optional: "optional",
      submit: "Send the request",
      sending: "Sending…",
      success: "Your request is on its way. We reply within 24 hours.",
      error: "We could not send your request. Try again or email us directly.",
      rateLimited: "You have already sent several requests. Try later or give us a call.",
      required: "Please fill in your name and a valid email.",
    },
    contact: {
      overline: "Reservations",
      title: "Ready for the plain?",
      body: "Tell us which game and season you have in mind, and we will build the right lowland hunt for you.",
      cta: "Get in touch",
      email: "bookings@lowlandhunting.ro",
    },
    footer: {
      tagline: "Lowland hunting estate.",
      rights: "All rights reserved.",
      cols: [
        { title: "Hunts", links: ["Pheasant", "Ducks & Geese", "Quail", "Hare"] },
        { title: "Estate", links: ["About us", "Ethics & rules", "Lodging", "Gallery"] },
        { title: "Contact", links: ["Reservations", "Phone", "Email", "Location"] },
      ],
    },
  },
  de: {
    nav: {
      links: [
        { label: "Start", href: "#top" },
        { label: "Preise", href: "#tarife" },
        { label: "Jagden", href: "#experiences" },
        { label: "Tradition", href: "#manifesto" },
        { label: "Kontakt", href: "#contact" },
      ],
      phone: "+40 726 000 000",
      menu: "Menü",
      close: "Schließen",
    },
    hero: {
      overline: "Jagdrevier · Rumänische Tiefebene",
      titleLines: ["Jagd", "im Flachland"],
      script: "Die Tradition der Ebene, im Morgenrot.",
      cta: "Preise ansehen",
      scroll: "Scrollen",
    },
    selection: {
      label: "Jagd wählen",
      fields: {
        region: {
          placeholder: "Region",
          options: ["Rumänische Tiefebene", "Donauaue", "Bărăgan"],
        },
        game: {
          placeholder: "Wild",
          options: ["Fasan", "Enten & Gänse", "Wachtel", "Feldhase"],
        },
        season: {
          placeholder: "Saison",
          options: ["Herbst", "Winter"],
        },
      },
      cta: "Suchen",
    },
    marquee: ["Jagd", "Tradition", "Ebene", "Morgenrot", "Stoppelfeld", "Aue"],
    manifesto: {
      overline: "Manifest",
      title: "Die Ebene hat ihr eigenes Ritual",
      chapters: [
        {
          no: "01",
          title: "Das Morgenrot",
          body: "Alles beginnt vor dem ersten Licht. Der Nebel hebt sich von den Stoppeln, das Feld atmet. Hier wird die Jagd nicht gehetzt — hier wird gewartet.",
        },
        {
          no: "02",
          title: "Der Hund",
          body: "Ein Partner, kein Werkzeug. Jedes Vorstehen, jede Nase im Stoppelfeld schreibt die Geschichte des Tages. Du folgst ihm und lernst die Ebene durch ihn.",
        },
        {
          no: "03",
          title: "Der Respekt",
          body: "Ethik, Maß und Sorgfalt für das Revier. Wir nehmen nur, was die Saison gibt, und hinterlassen die Ebene reicher, als wir sie vorgefunden haben.",
        },
      ],
    },
    experiences: {
      overline: "Jagdrouten",
      title: "Drei Jagden im Flachland",
      items: [
        {
          no: "01",
          title: "Fasan im Stoppelfeld",
          desc: "Klassische Jagd mit Vorstehhunden auf den weiten Stoppelfeldern der Rumänischen Tiefebene, im tiefen Licht des Morgens.",
          meta: [
            { k: "Dauer", v: "2 Tage" },
            { k: "Saison", v: "1. Okt — 15. Dez" },
            { k: "Revier", v: "≈ 1.200 ha" },
          ],
          cta: "Details zur Route",
        },
        {
          no: "02",
          title: "Enten in der Aue",
          desc: "Ansitz in der Donauaue, zwischen Teichen und Schilf, im Morgenrot und in der Dämmerung — auf Zugenten und Gänse.",
          meta: [
            { k: "Dauer", v: "3 Tage" },
            { k: "Saison", v: "15. Sept — 31. Jan" },
            { k: "Revier", v: "Teiche & Kanäle" },
          ],
          cta: "Details zur Route",
        },
        {
          no: "03",
          title: "Wachtel im Feld",
          desc: "Bewegliche Jagd in kleiner Gruppe auf dem offenen Feld des Bărăgan — dynamisch, sportlich, Schritt für Schritt mit dem Hund.",
          meta: [
            { k: "Dauer", v: "1 Tag" },
            { k: "Saison", v: "20. Aug — 30. Sept" },
            { k: "Revier", v: "≈ 800 ha" },
          ],
          cta: "Details zur Route",
        },
      ],
    },
    pricing: {
      overline: "Preise",
      title: "Was es kostet",
      body: "Die Preise gelten pro Jäger für das Standardpaket. Unterkunft ist nicht enthalten — wir sind eine Jagdagentur, keine Pension.",
      groups: {
        migratory: "Zugvögel",
        small_game: "Niederwild",
        big_game: "Hochwild",
      },
      season: "Saison",
      yearRound: "Ganzjährig",
      days: (n) => `${n} ${n === 1 ? "Jagdtag" : "Jagdtage"}`,
      outings: (n) => `${n} Ausgänge pro Tag`,
      group: "Gruppe",
      from: "ab",
      perHunter: "/ Jäger",
      perPiece: "/ Stück",
      onRequest: "Auf Anfrage",
      packageFrom: "Paket ab",
      includes: "Inklusive",
      trophyFee: "Abschussgebühr nach Trophäe",
      adder: "Aufschlag",
      cull: "Selektion",
      wounded: (pct) => `Angeschossenes, nicht gefundenes Wild wird mit ${pct}% des geschätzten Trophäenwerts berechnet.`,
      woundedFlat: (v) => `Angeschossenes, nicht gefundenes Wild wird mit ${v} € berechnet.`,
      missedShot: (v) => `Ein Fehlschuss wird mit ${v} € berechnet.`,
      empty: "Die Preise werden aktualisiert. Schreiben Sie uns für ein Angebot.",
      cta: "Angebot anfragen",
      configure: "Preis berechnen",
    },
    services: {
      transport_domain: "Transport innerhalb des Jagdgebiets",
      pro_guide: "Professioneller Jagdführer",
      guide_beaters_dogs: "Professioneller Jagdführer + Treiber mit Hunden",
      export_docs: "Dokumente für die Ausfuhr des Wildes",
      trophy_prep_export: "Trophäenpräparation und Ausfuhrdokumente",
      formalities: "Alle Formalitäten für die Jagd in Rumänien",
    },
    configurator: {
      overline: "Konfigurator",
      title: "Stellen Sie Ihre Jagd zusammen",
      body: "Wählen Sie das Wild, wie viele Sie sind und wie viele Tage. Der Preis erscheint sofort.",
      species: "Wild",
      hunters: "Jäger",
      days: "Tage",
      extras: "Zusatzoptionen",
      total: "Gesamt Gruppe",
      perHunter: "/ Jäger",
      custom: "Für diese Konfiguration erstellen wir Ihnen ein individuelles Angebot.",
      customCta: "Individuelles Angebot anfragen",
      cta: "Anfrage senden",
      note: "Die Preise beinhalten keine Unterkunft.",
    },
    seasons: {
      overline: "Gesetzlich",
      title: "Jagdzeiten und Tageslimits",
      body: "Die gesetzlichen Jagdzeiten und das tägliche Abschusslimit pro Jäger nach geltendem Recht.",
      species: "Wildart",
      period: "Jagdzeit",
      quota: "Tageslimit",
      pieces: "Stk.",
    },
    gallery: {
      overline: "Galerie",
      title: "Die Ebene in Bildern",
      body: "Landschaften und Momente aus den Revieren, in denen wir jagen.",
      close: "Schließen",
      prev: "Zurück",
      next: "Weiter",
    },
    form: {
      overline: "Anfrage",
      title: "Sagen Sie uns, was Sie vorhaben",
      name: "Name",
      email: "E-Mail",
      phone: "Telefon",
      guests: "Anzahl Jäger",
      message: "Nachricht",
      messagePlaceholder: "Welches Wild, welcher Zeitraum, wie viele Tage…",
      optional: "optional",
      submit: "Anfrage senden",
      sending: "Wird gesendet…",
      success: "Ihre Anfrage ist unterwegs. Wir antworten innerhalb von 24 Stunden.",
      error: "Wir konnten Ihre Anfrage nicht senden. Versuchen Sie es erneut oder schreiben Sie uns direkt per E-Mail.",
      rateLimited: "Sie haben bereits mehrere Anfragen gesendet. Versuchen Sie es später oder rufen Sie uns an.",
      required: "Bitte geben Sie Ihren Namen und eine gültige E-Mail-Adresse an.",
    },
    contact: {
      overline: "Reservierungen",
      title: "Bereit für die Ebene?",
      body: "Sagen Sie uns, welches Wild und welche Saison Sie im Sinn haben — wir stellen die passende Flachlandjagd für Sie zusammen.",
      cta: "Kontakt aufnehmen",
      email: "rezervari@probeniesto.ro",
    },
    footer: {
      tagline: "Jagdrevier im Flachland.",
      rights: "Alle Rechte vorbehalten.",
      cols: [
        { title: "Jagden", links: ["Fasan", "Enten & Gänse", "Wachtel", "Hase"] },
        { title: "Revier", links: ["Über uns", "Ethik & Regeln", "Unterkunft", "Galerie"] },
        { title: "Kontakt", links: ["Reservierungen", "Telefon", "E-Mail", "Standort"] },
      ],
    },
  },
};

export const brand = {
  name: "Pro Beniesto",
  tag: "Hunting Agency Romania",
  logo: "/logo-beni.png",
};

export const socials = [
  { platform: "facebook", label: "Facebook", href: "https://facebook.com" },
  { platform: "instagram", label: "Instagram", href: "https://instagram.com" },
  { platform: "youtube", label: "YouTube", href: "https://youtube.com" },
  { platform: "whatsapp", label: "WhatsApp", href: "https://wa.me/40726000000" },
];

export const images = {
  hero: heroImg,
  card1: card1Img,
  card2: card2Img,
  card3: card3Img,
  manifesto: manifestoImg,
  contact: contactImg,
};
